import React, { useState, useCallback, useMemo } from 'react';
import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { find, map, parseInt } from 'lodash';
import { useLocation } from '~/context/LocationContext';

import Row from '~/layout/Row';
import Column from '~/layout/Column';

import Image from '~/components/base/Image';
import BaseLink from '~/components/base/BaseLink';
import Icon from '~/components/base/Icon';
import Button from '~/components/base/Button';

import WashTypeBadge from '~/components/WashTypeBadge';
import UserContent from '~/components/UserContent';
import AccordionGroup from '~/components/AccordionGroup';
import Dropdown from '~/components/Dropdown';

import ConfirmWashLocation from '~/components/products/ConfirmWashLocation';
import AddOnsModal from '~/components/modal/AddOnsModal';

import { useCart } from '~/context/CartContext';

import ConfirmUpdateSubscriptionModal from '~/account/components/modals/ConfirmUpdateSubscriptionModal';
import QuantityButtons from '~/components/buttons/QuantityButtons';

import useProductPrice from '~/hooks/useProductPrice';

import { h1, h2, h4 } from '~/styles/typography';
import { calcVw } from '~/styles/helpers';

import { dynamicFontSize, formatText } from '~/utilities/helpers';
import { transition } from '~/styles/transition';

const ProductHeaderContainer = styled.section`
    position: relative;
    padding: 25px 0 0 0;

    &::before {
        position: absolute;
        content: '';
        top: 0;
        bottom: 0;
        left: 50%;
        right: 50%;
        margin: 0 ${calcVw(-50)};
        background-color: ${({ theme }) => theme.backgroundColors.lightBlue};
        background-size: contain;
        background-position: top;
        background-repeat: no-repeat;
        z-index: ${({ theme }) => theme.zIndex.backgroundGraphics};
    }

    &::after {
        position: absolute;
        content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 208 420'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath fill='%23FFF' d='M135.5 275C95.46 275 63 307.46 63 347.5S95.46 420 135.5 420s72.5-32.46 72.5-72.5-32.46-72.5-72.5-72.5m1 22c27.846 0 50.5 22.43 50.5 50s-22.654 50-50.5 50S86 374.57 86 347s22.654-50 50.5-50'/%3E%3Cg stroke-width='3'%3E%3Ccircle cx='102.5' cy='13.5' r='12' stroke='%231A488D'/%3E%3Ccircle cx='52.5' cy='253.5' r='12' stroke='%23ED3524'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        top: 5%;
        width: 25%;
        left: -${({ theme }) => theme.layout.gutter};

        @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
            top: 15%;
            width: 14%;
            left: calc(
                50% - ${calcVw(50)} + ${({ theme }) => theme.layout.largeGutter}
            );
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        padding: 0;
    }
`;

const MobileContent = styled.div`
    margin-bottom: 50px;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        display: none;
    }
`;

const DesktopContent = styled.div`
    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        display: block;
    }
`;

const ProductImageWrapper = styled.div`
    display: flex;
    align-items: ${({ imagePosition }) =>
        imagePosition === 'center' ? 'center' : 'flex-end'};
    height: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        padding: ${({ imagePosition }) =>
            imagePosition === 'center' ? '50px 0' : '50px 0 0'};
    }
`;

const ProductImage = styled(Image)`
    padding: 0 15px;
    margin: 0 auto 35px;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        padding: 0;
        margin-bottom: 0;
        ${({ theme, imagePosition }) =>
            imagePosition === 'bottom' &&
            `margin-left: -${theme.layout.gutter};`}
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        ${({ imagePosition }) =>
            imagePosition === 'bottom' && `margin-left: -135px;`}
    }
`;

const StyledWashtypeBadge = styled(WashTypeBadge)`
    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        justify-content: center;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        margin-top: 40px;
        justify-content: flex-start;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        margin-top: 90px;
    }
`;

const Title = styled.h1`
    ${h1};
    line-height: 1;
    color: ${({ theme }) => theme.colors.accent.blue};
    margin-top: 40px;
    margin-bottom: 10px;
    text-align: center;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        text-align: left;
        margin-top: 20px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        margin-top: 60px;
    }
`;

const Description = styled.p`
    ${h4};
    margin-bottom: 25px;
    text-align: center;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        text-align: left;
    }
`;

const Price = styled.p`
    ${h2};
    text-align: center;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        text-align: left;
        margin-top: 25px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        margin-top: 50px;
    }

    > span {
        position: relative;
        bottom: 1px;
        display: inline-block;
        margin-left: 5px;
        font-size: ${dynamicFontSize(1.8, 2, 2.4)};
    }
`;

const PromoIcon = styled(Icon)`
    margin-right: 10px;
    display: none;
    position: relative;
    top: 1px;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        display: inline-block;
    }
`;

const PromoBar = styled(BaseLink)`
    display: block;
    margin-top: 25px;
    text-align: center;
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    background-color: ${({ theme }) => theme.colors.text.black};
    color: ${({ theme }) => theme.colors.primary.white};
    font-size: ${dynamicFontSize(1.6, 2, 2.4)};
    padding: 10px 20px 10px 10px;

    > span {
        position: relative;
        top: 2px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        display: inline-block;
    }

    &:hover,
    &:focus {
        background-color: ${({ theme }) => theme.colors.accent.yellow};
        color: ${({ theme }) => theme.colors.text.black};
        ${transition({}, 'background-color', 'color')};

        ${PromoIcon} {
            svg * {
                color: ${({ theme }) => theme.colors.text.black};
            }
        }
    }
`;

const StyledButton = styled(Button)`
    margin-top: 25px;
    width: 100%;
    justify-content: center;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        margin-top: 0;
        width: auto;
    }
`;

const HeaderBottom = styled.div`
    margin-top: 50px;
    display: flex;
    gap: 10px;
    justify-content: space-between;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        margin-top: 25px;
        margin-bottom: 40px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        margin-top: 50px;
        margin-bottom: 90px;
    }
`;

const StyledAccordionGroup = styled(AccordionGroup)`
    position: relative;
    left: 50%;
    right: 50%;
    width: ${calcVw(100)};
    margin: 0 ${calcVw(-50)} 30px ${calcVw(-50)};
    border-left-width: 0 !important;
    border-right-width: 0 !important;
`;

const StyledUserContent = styled(UserContent)`
    li {
        &:not(:last-of-type) {
            margin-bottom: 10px;
        }
    }
`;

const DropdownText = styled.label`
    ${h4};
`;

const DropdownQuantityWrapper = styled.div``;

const QuantityWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 2rem;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        justify-content: left;
    }
`;

const InputFields = ({
    productType,
    setGiftCardValue,
    giftCardValues,
    giftCardValue,
    giftCardQuantity,
    currItem,
    setQuantity,
    price
}) => {
    const onChangeQuantity = useCallback(
        (quantity) => {
            setQuantity(quantity);
        },
        [setQuantity]
    );

    if (productType === 'gift-card') {
        const finalQuantity = giftCardQuantity || 1;

        return (
            <>
                <DropdownText htmlFor="gift-card-value">
                    Choose a gift card value:
                </DropdownText>
                <DropdownQuantityWrapper>
                    <Dropdown
                        buttonId="gift-card-value"
                        label={`$${giftCardValue.toString()}`}
                        options={map(giftCardValues, (value) => ({
                            label: `$${value.toString()}`,
                            action: () => setGiftCardValue(value),
                            selected: giftCardValue === value
                        }))}
                    />
                    <QuantityWrapper>
                        <QuantityButtons
                            min={finalQuantity}
                            step={finalQuantity}
                            defaultQuantity={
                                currItem?.quantity ?? finalQuantity
                            }
                            {...{ onChangeQuantity }}
                        />
                    </QuantityWrapper>
                </DropdownQuantityWrapper>
            </>
        );
    }

    return (
        <>
            <Price>{price}</Price>
            {productType === 'physical' ? (
                <QuantityWrapper>
                    <QuantityButtons
                        defaultQuantity={currItem?.quantity ?? 1}
                        {...{ onChangeQuantity }}
                    />
                </QuantityWrapper>
            ) : null}
        </>
    );
};

const ProductHeader = ({ product, productLocation, setProductLocation }) => {
    const { state: locationState } = useLocation();

    const {
        databaseId,
        title,
        displayName,
        featuredImage,
        productInformation: { productDescription, shortDescription, addOns },
        productImageSettings: { imagePosition },
        stripePrices,
        productTypes,
        giftCardValues,
        giftCardQuantity,
        cleaningCategories,
        productCategories
    } = product;

    const { addToCart, items, updateCartItemQuantity } = useCart();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [giftCardValue, setGiftCardValue] = useState(() => {
        return giftCardValues.length ? giftCardValues[0] : null;
    });

    const currItem = useMemo(() => {
        return find(items, (item) => {
            if (item.productType === 'gift-card') {
                return (
                    item.id === product.databaseId &&
                    item.giftCardValue === parseInt(giftCardValue)
                );
            }

            if (item.productType === 'physical') {
                return item.id === product.databaseId;
            }

            return false;
        });
    }, [product.databaseId, giftCardValue, items]);

    const price = useProductPrice(product, productLocation);

    const productType = productTypes?.nodes.length
        ? productTypes.nodes[0].slug
        : null;

    const priceId = stripePrices?.length
        ? find(stripePrices, { priceGroup: productLocation?.priceGroup })?.id
        : null;

    const addProductToCart = useCallback(async () => {
        return await addToCart(
            databaseId,
            displayName,
            productLocation,
            null,
            giftCardValue ? parseFloat(giftCardValue) * 100 : null,
            locationState?.vehicleID,
            locationState?.garageID,
            quantity,
            giftCardQuantity
        );
    }, [
        databaseId,
        addToCart,
        productLocation,
        giftCardValue,
        locationState,
        displayName,
        quantity,
        giftCardQuantity
    ]);

    const cartAction = useCallback(async () => {
        if (locationState?.editingPlan && locationState?.currentPlan) {
            setIsModalOpen(true);
        } else if (addOns && addOns.length) {
            setIsModalOpen(true);
        } else if (currItem) {
            await updateCartItemQuantity(
                currItem.cartItemId,
                quantity,
                productLocation
            );
            navigate('/cart');
        } else {
            await addProductToCart();
            navigate('/cart');
        }
    }, [
        setIsModalOpen,
        addOns,
        locationState,
        productLocation,
        addProductToCart,
        currItem,
        quantity,
        updateCartItemQuantity
    ]);

    const mobileAccordions = [
        {
            title: 'Confirm Your Wash Location',
            content: (
                <ConfirmWashLocation
                    location={productLocation}
                    setLocation={setProductLocation}
                />
            )
        }
    ];

    if (productDescription) {
        mobileAccordions.unshift({
            title: "What's Included",
            content: (
                <StyledUserContent
                    content={productDescription}
                    useChecklist={true}
                />
            )
        });
    }

    return (
        <ProductHeaderContainer>
            <Row>
                <Column span={12} spanLargeTablet={5}>
                    <ProductImageWrapper {...{ imagePosition }}>
                        {featuredImage && (
                            <ProductImage
                                {...featuredImage.node}
                                {...{ imagePosition }}
                                objectFit="contain"
                            />
                        )}
                    </ProductImageWrapper>
                </Column>

                <Column span={12} startLargeTablet={7} spanLargeTablet={6}>
                    <StyledWashtypeBadge
                        product={{ cleaningCategories, productCategories }}
                        useWhiteBackground={true}
                    />

                    <Title>
                        {formatText(displayName ? displayName : title)}
                    </Title>

                    <Description>{shortDescription}</Description>

                    <DesktopContent>
                        {productDescription && (
                            <StyledUserContent
                                content={productDescription}
                                useChecklist={true}
                            />
                        )}

                        <InputFields
                            {...{
                                productType,
                                setGiftCardValue,
                                giftCardValues,
                                giftCardValue,
                                giftCardQuantity,
                                currItem,
                                setQuantity,
                                price
                            }}
                        />

                        {productType === 'digital' && (
                            <PromoBar to="/product-category/unlimited-plans">
                                <PromoIcon
                                    icon="coin"
                                    size={22}
                                    color="yellow"
                                />
                                <span>
                                    Save time and money with an Unlimited Plan
                                </span>
                            </PromoBar>
                        )}

                        <HeaderBottom>
                            <ConfirmWashLocation
                                location={productLocation}
                                setLocation={setProductLocation}
                            />
                            <StyledButton
                                onClick={cartAction}
                                disabled={!productLocation}
                            >
                                {locationState?.editingPlan
                                    ? `Choose this plan`
                                    : currItem
                                      ? 'Update Quantity'
                                      : `Add to Cart`}
                            </StyledButton>
                        </HeaderBottom>
                    </DesktopContent>

                    <MobileContent>
                        <StyledAccordionGroup
                            accentColor="white"
                            accordions={mobileAccordions}
                        />

                        <InputFields
                            {...{
                                productType,
                                setGiftCardValue,
                                giftCardValues,
                                giftCardValue,
                                giftCardQuantity,
                                currItem,
                                setQuantity,
                                price
                            }}
                        />

                        <StyledButton
                            onClick={cartAction}
                            disabled={!productLocation}
                        >
                            {currItem ? 'Update Quantity' : 'Add to Cart'}
                        </StyledButton>
                        {productType === 'digital' && (
                            <PromoBar to="/product-category/unlimited-plans">
                                <PromoIcon
                                    icon="coin"
                                    size={22}
                                    color="yellow"
                                />
                                <span>
                                    Save time and money with an Unlimited Plan
                                </span>
                            </PromoBar>
                        )}
                    </MobileContent>
                </Column>
            </Row>

            {addOns && addOns.length && (
                <AddOnsModal
                    open={isModalOpen}
                    setOpen={setIsModalOpen}
                    productId={databaseId}
                    productDisplayName={displayName}
                    {...{ productLocation }}
                />
            )}

            {locationState?.editingPlan && (
                <ConfirmUpdateSubscriptionModal
                    setOpen={setIsModalOpen}
                    subscriptionName={formatText(
                        displayName ? displayName : title
                    )}
                    vehicleId={locationState.vehicleID}
                    garageId={locationState.garageID}
                    priceId={parseInt(priceId)}
                    open={isModalOpen}
                />
            )}
        </ProductHeaderContainer>
    );
};

ProductHeader.propTypes = {
    product: PropTypes.shape({
        databaseId: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        displayName: PropTypes.string,
        featuredImage: PropTypes.shape({
            node: PropTypes.shape({
                altText: PropTypes.string.isRequired,
                sourceUrl: PropTypes.string.isRequired,
                localFile: PropTypes.shape({
                    childImageSharp: PropTypes.object.isRequired
                })
            })
        }),
        productInformation: PropTypes.shape({
            productDescription: PropTypes.string,
            shortDescription: PropTypes.string,
            price: PropTypes.number,
            addOns: PropTypes.arrayOf(
                PropTypes.shape({
                    databaseId: PropTypes.number.isRequired,
                    title: PropTypes.string.isRequired,
                    addOnInformation: PropTypes.shape({
                        shortDescription: PropTypes.string.isRequired,
                        price: PropTypes.number.isRequired
                    })
                })
            )
        }).isRequired,
        productImageSettings: PropTypes.shape({
            imagePosition: PropTypes.string.isRequired
        }).isRequired,
        giftCardValues: PropTypes.arrayOf(PropTypes.number),
        cleaningCategories: PropTypes.shape({
            nodes: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string.isRequired
                })
            )
        }),
        productCategories: PropTypes.shape({
            nodes: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string.isRequired
                })
            )
        }),
        productTypes: PropTypes.shape({
            nodes: PropTypes.arrayOf(
                PropTypes.shape({
                    slug: PropTypes.string.isRequired
                })
            )
        }).isRequired,
        stripePrices: PropTypes.arrayOf(
            PropTypes.shape({
                priceGroup: PropTypes.number.isRequired,
                label: PropTypes.string.isRequired,
                stripeId: PropTypes.string.isRequired,
                id: PropTypes.string.isRequired
            })
        )
    }).isRequired
};
export default ProductHeader;
